<!--
	LegalPage — the whole of /legal/*. The four legal routes were byte-identical
	apart from which markdown they imported and their meta description, each
	carrying its own copy of ~70 lines of prose CSS; the styles are now in one
	place, which is the only way four documents stay looking like one document.

	Centered max-w-measure prose is the one sanctioned centered layout; Nav/Footer
	keep their normal treatment.
-->
<script lang="ts">
	import { parseLegalDoc } from '$lib/markdown';
	import Nav from './Nav.svelte';
	import Footer from './Footer.svelte';
	import { Section, MonoLabel, Rule } from '$lib/components/ui';
	import Meta from '$lib/seo/Meta.svelte';

	let {
		raw,
		description
	}: {
		/** The document's markdown source, imported `?raw` by the route. */
		raw: string;
		description: string;
	} = $props();

	const doc = $derived(parseLegalDoc(raw));
</script>

<Meta title={doc.title} {description} video={null} />

<Nav />
<main>
	<Section>
		<div class="wrap">
			<h1 class="title">{doc.title}</h1>
			{#if doc.updated}
				<MonoLabel class="mt-3 block">Last updated {doc.updated}</MonoLabel>
			{/if}
			<Rule class="mt-8" />
			<div class="legal-prose mt-8">
				<!-- eslint-disable-next-line svelte/no-at-html-tags -- build-time-only HTML from this repo's own markdown (src/lib/markdown.ts), never user input -->
				{@html doc.bodyHtml}
			</div>
		</div>
	</Section>
</main>
<Footer />

<style>
	.wrap {
		margin-inline: auto;
		max-width: var(--container-measure);
	}
	.title {
		font-family: var(--font-display);
		font-size: var(--text-h2);
		font-weight: 600;
		letter-spacing: -0.025em;
		color: var(--color-ink-900);
	}

	.legal-prose :global(h2) {
		margin-top: 2.5rem;
		font-family: var(--font-display);
		font-size: var(--text-h3);
		font-weight: 600;
		color: var(--color-ink-900);
	}
	.legal-prose :global(p) {
		margin-top: 1rem;
		color: var(--color-ink-500);
	}
	.legal-prose :global(ul),
	.legal-prose :global(ol) {
		margin-top: 1rem;
		padding-left: 1.25rem;
		color: var(--color-ink-500);
	}
	.legal-prose :global(ul) {
		list-style: disc;
	}
	.legal-prose :global(ol) {
		list-style: decimal;
	}
	.legal-prose :global(li + li) {
		margin-top: 0.5rem;
	}
	.legal-prose :global(strong) {
		color: var(--color-ink-900);
		font-weight: 600;
	}
	.legal-prose :global(code) {
		font-family: var(--font-mono);
		font-size: 0.9em;
		border-radius: var(--radius-sm);
		background-color: var(--color-panel);
		border: 1px solid var(--color-line);
		padding: 0.15em 0.4em;
	}
	.legal-prose :global(table) {
		display: block;
		margin-top: 1.5rem;
		max-width: 100%;
		overflow-x: auto;
		border-collapse: collapse;
		font-size: var(--text-mono-s);
	}
	.legal-prose :global(th),
	.legal-prose :global(td) {
		border: 1px solid var(--color-line);
		padding: 0.6rem 0.75rem;
		text-align: left;
		vertical-align: top;
		color: var(--color-ink-500);
	}
	.legal-prose :global(th) {
		font-family: var(--font-mono);
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-ink-400);
		background-color: var(--color-panel);
		white-space: nowrap;
	}
	.legal-prose :global(hr) {
		margin-block: 2rem;
		border: none;
		border-top: 1px solid var(--color-line);
	}
</style>
